import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ImagePlus, Loader2 } from "lucide-react";
import { BUSINESS, APPLIANCE_TYPES, BRANDS, TIME_WINDOWS } from "@/data/site";
import { CallButton } from "@/components/Blocks";
import { trackFormSubmit } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20";
const labelCls = "block text-sm font-semibold text-slate-800 mb-1.5";

const EMPTY = {
    name: "", phone: "", email: "", address: "", city: "", zip_code: "",
    appliance_type: "", appliance_brand: "", model_number: "", problem: "",
    preferred_date: "", preferred_time: "", is_property_manager: false, website: "",
};

export default function ServiceRequestForm() {
    const [form, setForm] = useState(EMPTY);
    const [photoTag, setPhotoTag] = useState(null);
    const [photoProblem, setPhotoProblem] = useState(null);
    const [status, setStatus] = useState("idle");

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v));
            if (photoTag) fd.append("photo_model_tag", photoTag);
            if (photoProblem) fd.append("photo_problem", photoProblem);
            await axios.post(`${API}/service-requests`, fd);
            trackFormSubmit("request_service_submit");
            setStatus("done");
        } catch (err) {
            setStatus("idle");
            toast.error("Something went wrong sending your request. Please call (321) 475-2230 and we'll take care of you.");
        }
    };

    if (status === "done") {
        return (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="request-success-panel">
                <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
                <h2 className="mt-4 font-heading text-2xl font-bold text-slate-900">Your request has been received</h2>
                <p className="mt-3 text-slate-700 max-w-md mx-auto leading-relaxed">
                    Thank you, <strong>{form.name.split(" ")[0]}</strong> — your service request has been received.
                    Appliance Mania will contact you to <strong>confirm availability and appointment details</strong> —
                    submitting this form does not confirm an appointment.
                    {form.email
                        ? <> A confirmation email is on its way to <strong>{form.email}</strong>.</>
                        : <> We'll reach you at the phone number you provided.</>}
                </p>
                <p className="mt-4 text-slate-700">Need immediate assistance?</p>
                <CallButton location="form-success" className="mt-3" testid="success-call-btn" />
                <p className="mt-4 text-xs text-slate-500">After-hours emergency service (10 PM – 6 AM): call {BUSINESS.phone}.</p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm" data-testid="service-request-form">
            <div className="grid gap-5 sm:grid-cols-2">
                <div>
                    <label className={labelCls} htmlFor="sr-name">Your name *</label>
                    <input id="sr-name" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Full name" data-testid="form-name-input" />
                </div>
                <div>
                    <label className={labelCls} htmlFor="sr-phone">Phone number *</label>
                    <input id="sr-phone" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(321) 555-0100" data-testid="form-phone-input" />
                </div>
                <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="sr-email">Email <span className="font-normal text-slate-500">(optional — for a confirmation email)</span></label>
                    <input id="sr-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="form-email-input" />
                </div>
                <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="sr-address">Service address *</label>
                    <input id="sr-address" required className={inputCls} value={form.address} onChange={set("address")} placeholder="Street address" data-testid="form-address-input" />
                </div>
                <div>
                    <label className={labelCls} htmlFor="sr-city">City *</label>
                    <input id="sr-city" required className={inputCls} value={form.city} onChange={set("city")} placeholder="Melbourne, Palm Bay, Sanford…" data-testid="form-city-input" />
                </div>
                <div>
                    <label className={labelCls} htmlFor="sr-zip">ZIP code *</label>
                    <input id="sr-zip" required inputMode="numeric" className={inputCls} value={form.zip_code} onChange={set("zip_code")} placeholder="32901" data-testid="form-zip-input" />
                </div>
                <div>
                    <label className={labelCls} htmlFor="sr-appliance">Appliance type *</label>
                    <select id="sr-appliance" required className={inputCls} value={form.appliance_type} onChange={set("appliance_type")} data-testid="form-appliance-select">
                        <option value="">Select appliance…</option>
                        {APPLIANCE_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                </div>
                <div>
                    <label className={labelCls} htmlFor="sr-brand">Appliance brand <span className="font-normal text-slate-500">(optional)</span></label>
                    <select id="sr-brand" className={inputCls} value={form.appliance_brand} onChange={set("appliance_brand")} data-testid="form-brand-select">
                        <option value="">Select if known…</option>
                        {BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                        <option value="Other">Other / not sure</option>
                    </select>
                </div>
                <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="sr-model">Model number <span className="font-normal text-slate-500">(optional)</span></label>
                    <input id="sr-model" className={inputCls} value={form.model_number} onChange={set("model_number")} placeholder="If known — usually on a tag inside the door or on the back" data-testid="form-model-input" />
                </div>
                <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="sr-problem">What's the appliance doing? *</label>
                    <textarea id="sr-problem" required rows={4} className={inputCls} value={form.problem} onChange={set("problem")}
                        placeholder="e.g. Refrigerator is warm but the freezer still works. There's a clicking sound every few minutes…" data-testid="form-problem-textarea" />
                </div>
                <div>
                    <label className={labelCls} htmlFor="sr-date">Preferred service date <span className="font-normal text-slate-500">(optional)</span></label>
                    <input id="sr-date" type="date" className={inputCls} value={form.preferred_date} onChange={set("preferred_date")} data-testid="form-date-input" />
                </div>
                <div>
                    <label className={labelCls} htmlFor="sr-time">Preferred time window <span className="font-normal text-slate-500">(optional)</span></label>
                    <select id="sr-time" className={inputCls} value={form.preferred_time} onChange={set("preferred_time")} data-testid="form-time-select">
                        <option value="">Select a window…</option>
                        {TIME_WINDOWS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>

                <div className="sm:col-span-2 grid gap-4 sm:grid-cols-2">
                    <PhotoField id="sr-photo-tag" label="Model/serial tag photo (optional)" hint="If available, a photo of the model/serial tag helps us identify your exact appliance" file={photoTag} onChange={setPhotoTag} testid="form-photo-tag-input" />
                    <PhotoField id="sr-photo-problem" label="Photo of the problem / error code (optional)" hint="A picture of the issue or display code" file={photoProblem} onChange={setPhotoProblem} testid="form-photo-problem-input" />
                </div>

                <div className="sm:col-span-2">
                    <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 cursor-pointer" data-testid="form-pm-checkbox-label">
                        <input type="checkbox" checked={form.is_property_manager} onChange={set("is_property_manager")}
                            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500" data-testid="form-pm-checkbox" />
                        <span className="text-sm text-slate-700">
                            <strong>I'm a property manager, landlord, or rental-property owner.</strong>
                            <span className="block mt-0.5 text-xs text-slate-500">Ask about $115 service calls, discounted repairs, and priority scheduling.</span>
                        </span>
                    </label>
                </div>
            </div>

            <div className="sr-only" aria-hidden="true">
                <label htmlFor="sr-website">Website</label>
                <input id="sr-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
            </div>

            <button type="submit" disabled={status === "sending"}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-bold px-6 py-4 rounded-xl shadow-lg shadow-red-600/25 transition-all text-base"
                data-testid="form-submit-btn">
                {status === "sending" ? (<><Loader2 className="h-5 w-5 animate-spin" /> Sending your request…</>) : "Submit Service Request"}
            </button>
            <p className="mt-3 text-xs text-slate-500 text-center leading-relaxed">
                Submitting this form sends a service <em>request</em> — it does not reserve or guarantee an appointment time.
                Appliance Mania will contact you to confirm. Need help now? Call <a href={BUSINESS.tel} className="text-red-600 font-semibold">{BUSINESS.phone}</a>.
            </p>
        </form>
    );
}

function PhotoField({ id, label, hint, file, onChange, testid }) {
    return (
        <div>
            <label className={labelCls} htmlFor={id}>{label}</label>
            <label htmlFor={id} className="flex items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3 cursor-pointer hover:border-red-400 hover:bg-red-50/40 transition-colors">
                <ImagePlus className="h-5 w-5 text-slate-400 shrink-0" />
                <span className="text-sm text-slate-600">{file ? file.name : hint}</span>
            </label>
            <input id={id} type="file" accept="image/*" className="sr-only" onChange={(e) => onChange(e.target.files?.[0] || null)} data-testid={testid} />
        </div>
    );
}
